import { trpc, trpcApi } from '@bitbot/api';
import { createStore } from 'hox';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useWallets } from '../wallet';
import { Notify } from '@bitbot/ui-design';
import { useSearchParams } from 'next/navigation';
import { useLogout } from '../user';

const DEFAULT_ACTIVE_INDEX = 0 as const

export const [useDex, DexProvider] = createStore(() => {
  const searchParams = useSearchParams();
  const [pairAddress, setPairAddress] = useState(
    searchParams.get('pairAddress'),
  );
  const { chain } = useWallets();
  const tabs = ['Overview', 'On-chain Data' /**, 'Trade'**/];
  const [activeIndex, setActiveIndex] = useState<number>(DEFAULT_ACTIVE_INDEX);
  const [token, setToken] = useState<string>();
  const [quoteToken, setQuoteToken] = useState<string>();
  const [flag, setFlag] = useState({ value: false, count: 0 });
  const [shit, setShit] = useState({ value: false, count: 0 });
  const [rocket, setRocket] = useState({ value: false, count: 0 });
  const [fav, setFav] = useState({ value: false, count: 0 });
  const [isInit, setIsInit] = useState(false);

  const {
    data: pairInfo,
    isSuccess: hasGotPairDetail,
    refetch: refetchPairInfo,
  } = trpc.market.getPairDetail.useQuery(
    {
      pairAddress: pairAddress!,
      chain: 'base',
    },
    {
      enabled: !!pairAddress && pairAddress !== '',
    },
  );

  useEffect(() => {
    if (pairInfo) {
      setToken(pairInfo.baseToken.symbol);
      setQuoteToken(pairInfo.quoteToken.symbol);
    }
    if (pairInfo && !isInit) {
      setFlag(pairInfo.reaction.redFlag);
      setShit(pairInfo.reaction.shit);
      setRocket(pairInfo.reaction.rocket);
      setFav(pairInfo.reaction.favor);
    }
  }, [pairInfo]);

  const reactionPairMutation = trpc.market.reactionPair.useMutation();
  const reactionPair = (
    pairAddress: string,
    reactionType: '💩' | '🚩' | '🚀' | '⭐',
    value: boolean,
  ) => {
    reactionPairMutation.mutateAsync({
      pairAddress,
      reactionType,
      value,
      chain: 'base',
    });
  };

  const { data: basePriceUsd } = trpc.wallet.getTokenPriceUsd.useQuery(
    {
      tokenAddress: pairInfo?.baseToken.address!,
      chain: chain.name,
    },
    {
      enabled: !!pairInfo?.baseToken.address,
    },
  );

  const { data: quotePriceUsd } = trpc.wallet.getTokenPriceUsd.useQuery(
    {
      tokenAddress: pairInfo?.quoteToken.address!,
      chain: chain.name,
    },
    {
      enabled: !!pairInfo?.quoteToken.address,
    },
  );

  const [isRateReverse, setIsRateReverse] = useState(false);

  const [delayCall, setDelayCall] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setDelayCall(true);
    }, 500);
  }, []);

  useEffect(() => {
    setIsRateReverse(false);
  }, [token, quoteToken]);

  const {
    data: recentTrades,
    isLoading: isGetingRecentTrades,
    refetch: refetchRecentTrades,
  } = trpc.market.getPairTrades.useQuery(
    {
      pairAddress: pairAddress!,
      chain: 'base',
    },
    {
      enabled: pairAddress !== '' && delayCall,
    },
  );

  return {
    token,
    quoteToken,
    isRateReverse,
    setIsRateReverse,
    pairInfo,
    pairAddress,
    setToken,
    setQuoteToken,
    setPairAddress,
    tabs,
    activeIndex,
    setActiveIndex,
    reactionPair,
    hasGotPairDetail,
    recentTrades,
    isGetingRecentTrades,
    basePriceUsd,
    quotePriceUsd,
    flag,
    setFlag,
    shit,
    setShit,
    fav,
    setFav,
    rocket,
    setRocket,
    isInit,
    setIsInit,
    refetchPairInfo,
    refetchRecentTrades,
  };
});

export const network = 'base';
export const tokens = [
  {
    token: 'ETH',
    icon: '/chain/eth.svg',
    token_address: '0x0000000000000000000000000000000000000000',
    name: 'Ethereum ',
    chain_icon: '/chain/base.png',
  },
  {
    token: 'USDC',
    icon: '/chain/usdc.svg',
    token_address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
    name: 'USDC',
    chain_icon: '/chain/base.png',
  },
];

type History = Awaited<ReturnType<typeof trpcApi.order.getAllOrder.query>>;

export const useOrderHistory = (
  state: 'open' | 'filled' | 'all',
  pagination = true,
) => {
  const { currentWallet, chain } = useWallets();
  const [cursor, setCursor] = useState<number | null>(1);
  const [history, setHistory] = useState<History>();
  const [isLoading, setIsLoading] = useState(true);
  const { handleInvalidToken } = useLogout();
  const getOrder = useCallback(
    debounce(() => {
      if (currentWallet && cursor !== null) {
        setIsLoading(true);
        trpcApi.order.getAllOrder
          .query({
            chain: chain.name,
            state,
            address: currentWallet!,
            cursor,
          })
          .then(data => {
            if (pagination) {
              if (data.length == 0) {
                setCursor(null);
              } else {
                setCursor(cursor + 1);
                if (history) {
                  setHistory([...history, ...data]);
                } else {
                  setHistory(data);
                }
              }
            } else {
              setHistory(data);
            }
          })
          .catch(e => {
            handleInvalidToken(e.message as string);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }, 400),
    [cursor],
  );

  useEffect(() => {
    if (currentWallet) {
      getOrder();
    }
  }, [currentWallet]);

  return { history, isLoading, getOrder, cursor };
};

export const useRecentTrades = () => {
  const { pairAddress } = useDex();
  const { data: recentTrades, isLoading } =
    trpc.market.getPairTrades.useQuery(
      {
        pairAddress: pairAddress!,
        chain: 'base',
      },
      {
        enabled: pairAddress !== '',
      },
    );
  return {
    recentTrades,
    isLoading,
  };
};

export const useOrderDetail = (id: string) => {
  const {
    data: orderInfo,
    isLoading,
    refetch,
  } = trpc.order.getOrder.useQuery(
    {
      orderid: id!,
    },
    {
      enabled: !!id,
    },
  );

  const cancelOrderMutation = trpc.order.cancelOrder.useMutation();
  const [isCanceling, setIsCanceling] = useState(false);
  const cancelOrder = () => {
    setIsCanceling(true);
    cancelOrderMutation
      .mutateAsync({ limitOrderId: id })
      .then(() => {
        refetch();
        Notify.success('Order canceled successfully');
      })
      .catch(error => {
        Notify.error(error.message);
      })
      .finally(() => {
        setIsCanceling(false);
      });
  };
  const _cancelOrder = debounce(cancelOrder, 300);
  return { orderInfo, isLoading, isCanceling, cancelOrder: _cancelOrder };
};
